import Dexie, { type Table } from 'dexie';
import dexieCloud from 'dexie-cloud-addon';

// ---------- Types ----------

export type Gender = 'male' | 'female' | 'other' | 'unknown';

export interface Person {
  id: string;                       // UUID — stable across trees for merge
  treeId: string;                   // owning tree
  realmId?: string;                 // Dexie Cloud realm (for sharing)
  owner?: string;                   // original contributor email
  name: string;                     // full or preferred
  label?: string;                   // display / short
  link?: string;                    // external source URL
  gender?: Gender;
  dob?: string;                     // ISO date or partial 'YYYY' / 'YYYY-MM'
  death?: string;                   // ISO or partial
  birthPlace?: string;
  deathPlace?: string;
  notes?: string;
  living?: boolean;                 // explicit override; otherwise computed
  livingOverride?: boolean;         // true = user set living manually
  publishAuthorized?: boolean;      // explicit consent to publish if living
  // Open-schema extension — anything not modelled above.
  extra?: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
}

export type RelationKind = 'parent' | 'spouse';

export interface Relationship {
  id: string;
  treeId: string;
  realmId?: string;
  kind: RelationKind;
  // For 'parent': a is parent, b is child.
  // For 'spouse': unordered pair; we still store a/b deterministically (sorted).
  a: string;
  b: string;
  startDate?: string;
  endDate?: string;
  createdAt: number;
  updatedAt: number;
}

export type TreeVisibility = 'private' | 'public';

export interface Tree {
  id: string;
  realmId?: string;                 // Dexie Cloud realm — present once shared
  owner: string;                    // email
  title: string;
  description?: string;
  rootPersonId?: string;
  visibility: TreeVisibility;       // default private
  createdAt: number;
  updatedAt: number;
}

export interface Comment {
  id: string;
  treeId: string;
  realmId?: string;
  personId: string;
  author: string;                   // email
  body: string;
  createdAt: number;
}

export type AuditAction =
  | 'person.create' | 'person.update' | 'person.delete'
  | 'rel.create'    | 'rel.delete'
  | 'tree.create'   | 'tree.update' | 'tree.share' | 'tree.unshare' | 'tree.publish' | 'tree.unpublish'
  | 'comment.create'| 'comment.delete'
  | 'merge.apply';

export interface AuditEntry {
  id: string;
  treeId?: string;
  realmId?: string;
  actor: string;                    // email
  action: AuditAction;
  targetId?: string;
  before?: unknown;
  after?: unknown;
  meta?: Record<string, unknown>;
  at: number;
}

// Admin approval gate — one row per signup.
// Dexie Cloud handles auth (email OTP); this table gates *app access*.
export interface UserProfile {
  id: string;                       // = email (lowercased)
  email: string;
  displayName?: string;
  role: 'admin' | 'user';
  approved: boolean;
  approvedBy?: string;
  approvedAt?: number;
  createdAt: number;
}

// ---------- DB ----------

class CousinsDB extends Dexie {
  people!:        Table<Person, string>;
  relationships!: Table<Relationship, string>;
  trees!:         Table<Tree, string>;
  comments!:      Table<Comment, string>;
  audit!:         Table<AuditEntry, string>;
  profiles!:      Table<UserProfile, string>;

  constructor() {
    // Renamed from 'linsmart' to 'cousins' on 2026-04-19 — clean break with
    // the demo-corrupted previous DB AND fixes the @id-on-profiles regression.
    super('cousins-v1', { addons: [dexieCloud] });

    // Plain `id` primary keys throughout — code generates its own UUIDs via
    // `newId()` for every record. Cannot use Dexie Cloud's '@id' (auto-managed)
    // because that disallows caller-supplied IDs, and we need stable cross-
    // device IDs for tree merging anyway.
    this.version(1).stores({
      people:        'id, treeId, realmId, name, owner, updatedAt',
      relationships: 'id, treeId, realmId, kind, a, b, updatedAt',
      trees:         'id, realmId, owner, visibility, updatedAt',
      comments:      'id, treeId, personId, realmId, author, createdAt',
      audit:         'id, treeId, realmId, actor, action, at',
      profiles:      'id, &email, role, approved, createdAt',
    });
  }
}

export const db = new CousinsDB();

// Configure Dexie Cloud once env var is set. Until then the DB works purely
// local — the app is offline-first by design, so this is a soft dependency.
const CLOUD_URL = import.meta.env.VITE_DEXIE_CLOUD_URL as string | undefined;
if (CLOUD_URL) {
  db.cloud.configure({
    databaseUrl: CLOUD_URL,
    requireAuth: false,             // we drive auth via authGate
    customLoginGui: true,
  });
}

export const hasCloud = Boolean(CLOUD_URL);
